/**
 * Allocation pie chart showing portfolio breakdown
 *
 * STATUS: PRESERVED BUT NOT CURRENTLY USED
 * See index.ts for full explanation.
 *
 * This component renders a donut chart showing allocation by account,
 * portfolio, or tag. Shows percentage labels and custom tooltips.
 * Potential uses: Admin dashboard, mobile views, email digests.
 */
import { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import type { AllocationItem } from '../../types/analytics';

// Extended type for chart data with index signature
interface ChartDataItem extends AllocationItem {
  [key: string]: string | number;
}

interface AllocationPieChartProps {
  data: AllocationItem[];
  height?: number;
}

// Color palette for pie chart segments
const COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
];

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: AllocationItem & { fill: string };
  }>;
}

const CustomTooltip = ({ active, payload }: TooltipProps) => {
  if (!active || !payload || !payload.length) return null;

  const item = payload[0].payload;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 mb-1">{item.name}</p>
      <p className="text-sm text-gray-600">
        Value: {formatCurrency(item.value)}
      </p>
      <p className="text-sm text-gray-600">
        Allocation: {item.percentage.toFixed(1)}%
      </p>
      <p className="text-sm text-gray-500">
        {item.holdings_count} holding{item.holdings_count !== 1 ? 's' : ''}
      </p>
    </div>
  );
};


// Custom label renderer that accesses our data
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderCustomLabel = (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  if (!midAngle || !percent) return null;

  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null; // Don't show labels for small slices

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export function AllocationPieChart({
  data,
  height = 300,
}: AllocationPieChartProps) {
  const chartData: ChartDataItem[] = useMemo(() => {
    // Sort by value descending and limit to top items
    return data
      .sort((a, b) => b.value - a.value)
      .slice(0, COLORS.length)
      .map((item, index) => ({
        ...item,
        fill: COLORS[index % COLORS.length],
      }));
  }, [data]);

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Build custom legend from data
  const renderLegend = () => (
    <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
      {chartData.map((entry, index) => (
        <li key={index} className="flex items-center text-xs">
          <span
            className="w-3 h-3 rounded-full mr-1"
            style={{ backgroundColor: COLORS[index % COLORS.length] }}
          />
          <span className="text-gray-600">{entry.name}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="45%"
          innerRadius={60}
          outerRadius={90}
          paddingAngle={2}
          dataKey="value"
          nameKey="name"
          label={renderCustomLabel}
          labelLine={false}
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={COLORS[index % COLORS.length]}
              stroke="white"
              strokeWidth={2}
              name={entry.name}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend content={renderLegend} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default AllocationPieChart;
